import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type SubmitStatus = 'success' | 'error' | null;

export default function Contact() {
  const { ref, isVisible } = useScrollAnimation();
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>(null);
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (messageLen > 500) return;
    setSubmitting(true);
    setStatus(null);

    const formData = new FormData(e.currentTarget);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6tcg84j8d20juc90190', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        (e.target as HTMLFormElement).reset();
        setMessageLen(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full bg-[#F7F9F7] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-eco-green transition-colors';

  return (
    <section id="kontakt" className="py-28 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-8">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

            {/* Left: Info */}
            <div className="flex flex-col justify-between">
              <div>
                <span className="inline-flex items-center gap-2 text-eco-green text-xs md:text-4xl font-bold uppercase tracking-[0.2em] mb-5">
                  <div className="w-6 h-0.5 bg-eco-green" />
                  Kontakt
                </span>
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                  Võta meiega <span className="text-eco-green">ühendust</span>
                </h2>
                <p className="text-gray-500 text-[15px] leading-relaxed mb-10 max-w-md">
                  Vajad lahendust ehitusjäätmete käitlemiseks? Küsi pakkumist — vastame tavaliselt samal päeval.
                </p>

                <div className="space-y-5 mb-10">
                  <a
                    href="tel:+37253400444"
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-pale-green text-eco-green flex-shrink-0 group-hover:bg-eco-green group-hover:text-white transition-colors">
                      <i className="ri-phone-line text-xl" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">Telefon</div>
                      <div className="text-base font-bold text-gray-800 group-hover:text-eco-green transition-colors">
                        +372 53 400 444
                      </div>
                    </div>
                  </a>

                  <a
                    href="mailto:recycling@iisral.eu"
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-pale-green text-eco-green flex-shrink-0 group-hover:bg-eco-green group-hover:text-white transition-colors">
                      <i className="ri-mail-line text-xl" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">E-post</div>
                      <div className="text-base font-bold text-gray-800 group-hover:text-eco-green transition-colors">
                        recycling@iisral.eu
                      </div>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-pale-green text-eco-green flex-shrink-0">
                      <i className="ri-map-pin-line text-xl" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">Teenindusala</div>
                      <div className="text-base font-bold text-gray-800">Üle Eesti</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tagline card */}
              <div className="bg-dark-green rounded-2xl p-7 flex items-center gap-5">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-leaf-line text-3xl text-eco-green" />
                </div>
                <div>
                  <div className="text-eco-green text-sm font-bold uppercase tracking-wider mb-1">
                    Mõtleme roheliselt
                  </div>
                  <div className="text-white/70 text-xs leading-relaxed">
                    Keskkonnasõbralikud lahendused Sinu projektile
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-3xl p-10 border border-gray-100">
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Küsi pakkumist</h3>
              <p className="text-gray-400 text-sm mb-7">Täida vorm ja võtame sinuga ühendust.</p>

              {status === 'success' && (
                <div className="bg-pale-green border border-eco-green/20 rounded-xl px-5 py-4 mb-6 flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-eco-green text-xl" />
                  <p className="text-dark-green text-sm font-semibold">
                    Täname! Võtame Sinuga peagi ühendust.
                  </p>
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-5 py-4 mb-6 flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl" />
                  <p className="text-red-600 text-sm font-semibold">
                    Saatmine ebaõnnestus. Proovi uuesti.
                  </p>
                </div>
              )}

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Nimi *
                    </label>
                    <input
                      type="text"
                      name="nimi"
                      required
                      placeholder="Sinu nimi"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="telefon"
                      placeholder="+372 ..."
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    E-post *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="sinu@email.ee"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Teenus
                  </label>
                  <select name="teenus" className={inputClass}>
                    <option value="">Vali teenus</option>
                    <option value="Ehitusjäätmete käitlemine">Ehitusjäätmete käitlemine objektil</option>
                    <option value="Vahekoristus">Ehitusaegne vahekoristus</option>
                    <option value="Abitööliste rent">Abitööliste rent</option>
                    <option value="Lõppkoristus">Lõppkoristus</option>
                    <option value="Muu">Muu</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Sõnum *
                    <span className={`ml-2 normal-case font-normal ${messageLen > 500 ? 'text-red-400' : 'text-gray-300'}`}>
                      {messageLen}/500
                    </span>
                  </label>
                  <textarea
                    name="sonnum"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Kirjelda oma projekti või küsimust..."
                    className={`${inputClass} resize-none`}
                    onChange={(e) => setMessageLen(e.target.value.length)}
                  />
                  {messageLen > 500 && (
                    <p className="text-red-400 text-xs mt-1">Sõnum on liiga pikk (max 500 tähemärki).</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || messageLen > 500}
                  className="w-full bg-eco-green text-white font-bold text-sm py-4 rounded-xl cursor-pointer whitespace-nowrap transition-all hover:bg-dark-green disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Saadan...
                    </>
                  ) : (
                    <>
                      Küsi pakkumist
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
